import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip, TextField,
  Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Autocomplete, Switch, InputAdornment, Select, MenuItem, FormControl,
  ToggleButton, ToggleButtonGroup, Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  EventRepeat as SubsIcon, Delete as DeleteIcon,
  Class as ClassIcon, Payments as IncomeIcon,
  Person as PersonIcon, Search as SearchIcon, AutoAwesome as AutoIcon,
  Check as CheckIcon, LocalOffer as PackageIcon,
  Gavel as RuleIcon, EventBusy as AbsentIcon, MoneyOff as NoPayIcon,
  HelpOutline as UnmarkedIcon, Save as SaveIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const AUTOSAVE_MS = 500;
// There is deliberately no "all classes" option any more.
//
// The page used to open on every learner in the school and fetch the lot
// before anyone had chosen anything. In a school of a few thousand that is
// a few thousand rows — each with an arrears lookup, an amount box, a
// package picker and a switch — built on page load, every load. It made
// the screen slow to appear and then slow to type in.
//
// Class is now asked for FIRST and is required; the income type only opens
// once a class is chosen. Nothing is fetched until both are set, and what
// is fetched is one class.

// Today as YYYY-MM-DD in local time, used as the default start_date.
const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Charging rule ───────────────────────────────────────────────
// When does a day's amount actually become a debt? Schools answer that
// differently depending on what is being collected, and getting it wrong
// either invents arrears nobody owes or lets real fees go unbilled.
//
//   Daily school fees are owed because the name is on the roll. Stay
//   home all week and you still owe five days.
//
//   Feeding is owed because the learner ate. No school, no meal, nothing
//   owed — and a learner who came, did not eat and did not pay owes
//   nothing either.
//
// Two switches cover both, and the presets below are just the two named
// combinations. Kept as data so the wording lives in one place.
const DEFAULT_RULES = {
  charge_when_absent: 0, charge_when_unpaid: 1, bill_unmarked_at_close: 0,
  auto_settle_days: 0, auto_settle_from: null,
};

const RULE_PRESETS = [
  {
    key: 'compulsory',
    label: 'Compulsory',
    hint: 'Daily school fees',
    rules: { charge_when_absent: 1, charge_when_unpaid: 1 },
    blurb: 'Owed because the name is on the roll. Miss school, come without '
         + 'money, come and pay nothing — the day still counts, and the '
         + 'arrears keep adding up every school day.',
  },
  {
    key: 'only_when_used',
    label: 'Only when used',
    hint: 'Feeding, transport',
    rules: { charge_when_absent: 0, charge_when_unpaid: 0 },
    blurb: 'Owed only when the learner actually takes it. Absent means no '
         + 'meal and no ride, so nothing is owed — and a learner who came, '
         + 'did not eat and did not pay owes nothing either.',
  },
];

const ruleKey = (r) => {
  const a = Number(r?.charge_when_absent) ? 1 : 0;
  const u = Number(r?.charge_when_unpaid) ? 1 : 0;
  if (a && u)   return 'compulsory';
  if (!a && !u) return 'only_when_used';
  return 'custom';
};

const sameRules = (a, b) =>
  ['charge_when_absent', 'charge_when_unpaid', 'bill_unmarked_at_close',
   'auto_settle_days']
    .every(k => (Number(a?.[k]) ? 1 : 0) === (Number(b?.[k]) ? 1 : 0))
  && (a?.auto_settle_from || null) === (b?.auto_settle_from || null);

export default function DailySubscriptionsPage() {
  // Reference data
  const [classes, setClasses]         = useState([]);
  const [incomeTypes, setIncomeTypes] = useState([]);

  // Filters that drive the roster query. Class comes first and starts
  // empty — picking it is the first thing the page asks for.
  const [incomeType, setIncomeType]   = useState(null);
  const [classFilter, setClassFilter] = useState(null);
  const [startDate, setStartDate]     = useState(todayISO());
  const [defaultAmount, setDefaultAmount] = useState('');
  const [search, setSearch]           = useState('');

  // Unified roster: every student in scope, subscribed or not.
  const [roster, setRoster]     = useState([]);
  // Active packages available for the picked income type. Empty ⇒ this
  // income type doesn't use tiers; the Package column stays hidden and
  // the table looks exactly like it did before packages existed.
  const [packages, setPackages] = useState([]);
  const [loading, setLoading]   = useState(false);

  // The charging rule for the picked income type: `saved` is what the
  // server holds, `draft` is what the switches show. Two copies because
  // this is a policy for every learner on the collection, not a per-row
  // edit — it saves on an explicit button, and the button has to know
  // whether anything actually changed.
  const [savedRules, setSavedRules] = useState(DEFAULT_RULES);
  const [draftRules, setDraftRules] = useState(DEFAULT_RULES);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesSavedAt, setRulesSavedAt] = useState(0);
  const [rulesOpen, setRulesOpen] = useState(false);

  // Per-row UI state: draft amount, saving spinner, last save timestamp,
  // and an error string. Keyed by student_id.
  const [rowState, setRowState] = useState({});

  // "Fill blank" writes the whole roster in one request; the button has
  // to say so and refuse a second press while it is in flight, or an
  // impatient double-click sends the same batch twice.
  const [bulkFilling, setBulkFilling] = useState(false);

  // One debounce timer per student so keystrokes on different rows don't
  // stomp on each other's pending saves.
  const timersRef = useRef({});

  // ── Loaders ────────────────────────────────────────────────
  const loadRefs = async () => {
    try {
      const [c, it] = await Promise.all([
        API.get('/classes'),
        API.get('/collections/income-types'),
      ]);
      setClasses(c.data || []);
      setIncomeTypes(it.data || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load reference data'));
    }
  };

  const loadRoster = async () => {
    // Both, or nothing. The class is what keeps this to one small query.
    if (!classFilter || !incomeType) {
      setRoster([]); setPackages([]); setRowState({}); return;
    }
    setLoading(true);
    try {
      const r = await API.get('/daily-subscriptions/roster', {
        params: { income_type: incomeType.type, cla: classFilter },
      });
      // Response shape: { rows: [...], packages: [...], charge_rules: {...} }
      const rows = Array.isArray(r.data?.rows) ? r.data.rows : (Array.isArray(r.data) ? r.data : []);
      const pkgs = Array.isArray(r.data?.packages) ? r.data.packages : [];
      setRoster(rows);
      setPackages(pkgs);
      // Arrives with the roster rather than as its own request — one
      // fewer round-trip on a page whose whole job is one income type.
      const cr = { ...DEFAULT_RULES, ...(r.data?.charge_rules || {}) };
      setSavedRules(cr);
      setDraftRules(cr);
      setRulesSavedAt(0);
      // Seed rowState from the fresh roster — registered rows show their
      // saved amount, unregistered rows start blank.
      const seed = {};
      for (const row of rows) {
        seed[row.student_id] = {
          amount: row.subscription_id ? String(Number(row.daily_amount) || 0) : '',
          saving: false, savedAt: 0, error: '',
        };
      }
      setRowState(seed);
      // Handed back so a caller that has just written can check what the
      // server actually stored, rather than trusting its own optimism.
      return rows;
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load roster'));
      return null;
    } finally { setLoading(false); }
  };

  useEffect(() => { loadRefs(); }, []);
  useEffect(() => { loadRoster(); /* eslint-disable-next-line */ }, [incomeType, classFilter]);

  // ── Derived ────────────────────────────────────────────────
  const classOptions = useMemo(
    () => classes.map(c => c.class).filter(Boolean),
    [classes]
  );
  const incomeOptions = useMemo(
    () => incomeTypes.map(t => ({ trans: t.trans, type: t.type, cat: t.cat })),
    [incomeTypes]
  );

  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roster;
    return roster.filter(r =>
      (r.student_name || '').toLowerCase().includes(q) ||
      (r.student_id   || '').toLowerCase().includes(q));
  }, [roster, search]);

  // ── How many rows are actually drawn ────────────────────────────
  //
  // The page opens on "All classes", so the first load is every learner
  // in the school — up to 2,000 rows, each with an amount box, a
  // package picker and a switch. Drawing all of that is what makes the
  // screen take an age to appear and then stutter while typing.
  //
  // A page at a time fixes it at the cause. The counts above the table
  // still describe the WHOLE roster, so "26 registered · 0 pending"
  // stays true regardless of how much is on screen.
  const PAGE = 40;
  const [visibleCount, setVisibleCount] = useState(PAGE);

  // Start again whenever the list underneath changes, or a search that
  // matches learner 900 would come back empty.
  useEffect(() => { setVisibleCount(PAGE); }, [search, classFilter, incomeType]);

  const pageRoster  = useMemo(
    () => filteredRoster.slice(0, visibleCount), [filteredRoster, visibleCount]);
  const hiddenCount = Math.max(0, filteredRoster.length - pageRoster.length);

  const stats = useMemo(() => {
    let registered = 0, arrears = 0, credit = 0;
    for (const r of roster) {
      if (r.subscription_id) registered += 1;
      arrears += Number(r.arrears) || 0;
      credit  += Number(r.credit)  || 0;
    }
    return { registered, unregistered: roster.length - registered, arrears, credit };
  }, [roster]);

  // ── Charging rule ──────────────────────────────────────────
  // Flipping a switch only moves the draft. Nothing reaches the server
  // until Save is pressed: this decides what every learner on the
  // collection owes from tomorrow on, so it should not follow the
  // roster's autosave habit where a mis-tap is instantly policy.
  const setRule = (patch) => {
    setDraftRules(prev => {
      const next = { ...prev, ...patch };
      // Billing the unmarked is meaningless when absentees are not
      // charged — it would write rows worth nothing. The backend enforces
      // the same thing; mirrored here so the switch visibly lets go
      // instead of silently disagreeing with what gets saved.
      if (!Number(next.charge_when_absent)) next.bill_unmarked_at_close = 0;
      // And settling unworked days rests on billing the unmarked, so it
      // lets go with it. Same rule the backend applies.
      if (!Number(next.bill_unmarked_at_close)) next.auto_settle_days = 0;
      return next;
    });
    setRulesSavedAt(0);
  };

  const rulesDirty = !sameRules(savedRules, draftRules);

  const saveRules = async () => {
    if (!incomeType) return;
    setRulesSaving(true);
    try {
      const r = await API.put(
        `/daily-subscriptions/charge-rules/${encodeURIComponent(incomeType.type)}`,
        {
          charge_when_absent:     Number(draftRules.charge_when_absent)     ? 1 : 0,
          charge_when_unpaid:     Number(draftRules.charge_when_unpaid)     ? 1 : 0,
          bill_unmarked_at_close: Number(draftRules.bill_unmarked_at_close) ? 1 : 0,
          auto_settle_days:       Number(draftRules.auto_settle_days)       ? 1 : 0,
          auto_settle_from:       draftRules.auto_settle_from || null,
        }
      );
      const cr = { ...DEFAULT_RULES, ...(r.data || {}) };
      setSavedRules(cr);
      setDraftRules(cr);
      setRulesSavedAt(Date.now());
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not save the charging rule'));
    } finally { setRulesSaving(false); }
  };

  // ── Autosave core ──────────────────────────────────────────
  // Every keystroke on a row: (1) mirror into rowState so the input stays
  // responsive, (2) reset that row's debounce timer. When the timer fires
  // we upsert to the backend and patch the row in place — no full reload,
  // so a fast typist can move down the list without seeing flicker.
  const scheduleSave = (row, newAmount) => {
    setRowState(prev => ({
      ...prev,
      [row.student_id]: {
        ...(prev[row.student_id] || {}),
        amount: newAmount, error: '',
      },
    }));
    if (timersRef.current[row.student_id]) {
      clearTimeout(timersRef.current[row.student_id]);
    }
    timersRef.current[row.student_id] = setTimeout(() => {
      saveRow(row, newAmount);
    }, AUTOSAVE_MS);
  };

  // Core upsert. `extras` may carry { package_id, package_label } when
  // the caller wants to save those alongside the amount in one round-trip
  // (e.g. from the Package dropdown handler). Passing amountStr = ''
  // is still a no-op — dedicated Delete icon un-registers.
  const saveRow = async (row, amountStr, extras) => {
    if (amountStr === '' || amountStr == null) return;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount < 0) {
      setRowState(prev => ({
        ...prev,
        [row.student_id]: { ...(prev[row.student_id] || {}), error: 'Invalid amount' },
      }));
      return;
    }
    // Skip the round-trip only if BOTH amount and package are unchanged.
    if (row.subscription_id
        && Number(row.daily_amount) === amount
        && (!extras || (
          (extras.package_id ?? null) === (row.package_id ?? null)
          && (extras.package_label ?? null) === (row.package_label ?? null)
        ))) return;

    setRowState(prev => ({
      ...prev,
      [row.student_id]: { ...(prev[row.student_id] || {}), saving: true, error: '' },
    }));

    try {
      let newSubId = row.subscription_id;
      if (row.subscription_id) {
        const body = { daily_amount: amount };
        if (extras) {
          body.package_id    = extras.package_id ?? null;
          body.package_label = extras.package_label ?? null;
        }
        await API.put(`/daily-subscriptions/${row.subscription_id}`, body);
      } else {
        const res = await API.post('/daily-subscriptions/bulk', {
          income_type: incomeType.type,
          start_date:  startDate,
          students: [{
            student_id:    row.student_id,
            student_name:  row.student_name,
            class_name:    row.class_name,
            daily_amount:  amount,
            package_id:    extras?.package_id    ?? null,
            package_label: extras?.package_label ?? null,
          }],
        });
        newSubId = res.data?.created?.[0]?.insertId || null;
      }
      // Patch the roster row in place — the row is now registered with
      // the new amount, but arrears/credit are unchanged (backend has
      // no side-effect on them from a simple amount edit).
      setRoster(rs => rs.map(r => r.student_id === row.student_id
        ? { ...r,
            subscription_id: newSubId,
            daily_amount:    amount,
            active:          1,
            ...(extras ? {
              package_id:    extras.package_id    ?? null,
              package_label: extras.package_label ?? null,
            } : {}),
          }
        : r));
      setRowState(prev => ({
        ...prev,
        [row.student_id]: { ...(prev[row.student_id] || {}), saving: false, savedAt: Date.now() },
      }));
    } catch (e) {
      setRowState(prev => ({
        ...prev,
        [row.student_id]: {
          ...(prev[row.student_id] || {}),
          saving: false,
          error: swal.pickError(e, 'Save failed'),
        },
      }));
    }
  };

  // Picking a package: auto-fills the amount with the package default and
  // saves both fields in one PUT/POST. Passing '' clears the package (leaves
  // amount intact — no reason to erase a saved daily rate for a null pick).
  const assignPackage = (row, packageIdStr) => {
    const pkgId = packageIdStr === '' || packageIdStr == null
      ? null : Number(packageIdStr);
    const pkg   = pkgId ? packages.find(p => p.id === pkgId) : null;
    // Clearing: just clear the reference; keep whatever amount is stored.
    if (!pkg) {
      // Only meaningful when there's an existing sub — otherwise nothing to unlink.
      if (!row.subscription_id) {
        setRoster(rs => rs.map(r => r.student_id === row.student_id
          ? { ...r, package_id: null, package_label: null } : r));
        return;
      }
      API.put(`/daily-subscriptions/${row.subscription_id}`,
        { package_id: null, package_label: null })
        .then(() => {
          setRoster(rs => rs.map(r => r.student_id === row.student_id
            ? { ...r, package_id: null, package_label: null } : r));
        })
        .catch(e => swal.error(swal.pickError(e, 'Could not clear package')));
      return;
    }
    // Assigning: auto-fill the amount (also updates the visible input) and
    // upsert with both fields together so first-time picks register in one shot.
    const amt = String(Number(pkg.default_amount) || 0);
    setRowState(prev => ({
      ...prev,
      [row.student_id]: {
        ...(prev[row.student_id] || {}), amount: amt, error: '',
      },
    }));
    saveRow(row, amt, { package_id: pkg.id, package_label: pkg.label });
  };

  const toggleActive = async (row) => {
    if (!row.subscription_id) return;
    try {
      await API.put(`/daily-subscriptions/${row.subscription_id}`,
        { active: row.active ? 0 : 1 });
      setRoster(rs => rs.map(r => r.student_id === row.student_id
        ? { ...r, active: r.active ? 0 : 1 }
        : r));
    } catch (e) { swal.error(swal.pickError(e, 'Toggle failed')); }
  };

  const removeRow = async (row) => {
    if (!row.subscription_id) return;
    const ok = await swal.confirmDelete(
      `${row.student_name || row.student_id} from ${incomeType?.type || 'this collection'}`);
    if (!ok) return;
    try {
      await API.delete(`/daily-subscriptions/${row.subscription_id}`);
      // Row stays in the roster but reverts to unregistered state so
      // the admin can type a fresh amount immediately if they want.
      setRoster(rs => rs.map(r => r.student_id === row.student_id
        ? { ...r, subscription_id: null, daily_amount: 0, active: 0,
            package_id: null, package_label: null }
        : r));
      setRowState(prev => ({
        ...prev,
        [row.student_id]: { amount: '', saving: false, savedAt: 0, error: '' },
      }));
    } catch (e) { swal.error(swal.pickError(e, 'Delete failed')); }
  };

  // Fill every blank row with the default amount.
  //
  // ONE request for the whole batch. This used to fire a separate save
  // per learner, 40ms apart — on a roster of 230 that is 230 HTTP
  // requests and 230 separate database transactions, each doing
  // INSERT ... ON DUPLICATE KEY UPDATE against the same unique index.
  // The browser only runs six at a time, the pool runs out, and rows
  // that lost a lock or timed out failed quietly into per-row error
  // state — so some learners registered, some silently stayed
  // "Pending", and which ones varied run to run.
  //
  // /daily-subscriptions/bulk already takes the whole array and does it
  // in a single transaction: all of them register, or none do, and any
  // failure is one visible error instead of a scatter of silent ones.
  const applyDefaultToBlank = async () => {
    if (!defaultAmount || Number(defaultAmount) <= 0) {
      return swal.error('Enter a positive default amount first', 'No amount set');
    }
    const targets = roster.filter(r => !r.subscription_id
      && !rowState[r.student_id]?.amount);
    if (targets.length === 0) {
      return swal.info?.('No blank rows to fill') || swal.error('No blank rows', 'Nothing to fill');
    }

    // Any per-row debounce already ticking for these rows would fire a
    // second, competing write for the same learner right after this one.
    for (const r of targets) {
      if (timersRef.current[r.student_id]) {
        clearTimeout(timersRef.current[r.student_id]);
        delete timersRef.current[r.student_id];
      }
    }

    setBulkFilling(true);
    try {
      await API.post('/daily-subscriptions/bulk', {
        income_type: incomeType.type,
        start_date:  startDate,
        students: targets.map(r => ({
          student_id:   r.student_id,
          student_name: r.student_name,
          class_name:   r.class_name,
          daily_amount: Number(defaultAmount),
        })),
      });

      // Re-read the roster and count what the SERVER says, rather than
      // marking rows registered because the request did not throw. The
      // whole failure being fixed here was rows that looked one way on
      // screen and another in the database, so the screen is rebuilt
      // from the database. It also keeps this independent of the shape
      // of the bulk reply, which an older backend may not match.
      const rows = await loadRoster();
      if (Array.isArray(rows)) {
        const sent = new Set(targets.map(r => String(r.student_id)));
        const stillPending = rows.filter(
          r => sent.has(String(r.student_id)) && !r.subscription_id).length;
        if (stillPending > 0) {
          swal.error(
            `${targets.length - stillPending} of ${targets.length} learners were `
            + `registered. ${stillPending} are still pending — press the button `
            + `again to finish them.`,
            'Not everyone was registered');
        }
      }
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not register the learners'),
        'Nothing was registered');
    } finally { setBulkFilling(false); }
  };

  return (
    <Box sx={{ maxWidth: 1720, mx: 'auto', fontFamily: FONT_ACCT, px: 1 }}>
      <ModuleHeader
        icon={<SubsIcon />}
        title="Collection Registration"
        subtitle="Pick a class, then an income type — that class appears below. Type a daily amount; it saves automatically."
      />

      {/* Filter bar — compact single row on wide screens */}
      <Card sx={{ mb: 1.5, borderRadius: 2 }}>
        <Box sx={{
          px: 2.5, py: 1.8, display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 1.2fr 1fr 1fr' },
          columnGap: 1.5, rowGap: 1.2, alignItems: 'center',
        }}>
          {/* Class FIRST. Everything downstream is scoped to it, and
              nothing is fetched until it is set. */}
          <Autocomplete size="small" options={classOptions}
            value={classFilter}
            onChange={(_, v) => setClassFilter(v)}
            renderInput={(p) => (
              <TextField {...p} label="Class" required
                autoFocus
                helperText="Pick the class first"
                InputProps={{ ...p.InputProps,
                  startAdornment: (
                    <>
                      <ClassIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                      {p.InputProps.startAdornment}
                    </>
                  ) }} />
            )} />

          {/* Then the income type — closed until a class is chosen, so
              the heavy query cannot be triggered by picking this alone. */}
          <Autocomplete size="small" options={incomeOptions}
            groupBy={(o) => o.cat}
            value={incomeType}
            onChange={(_, v) => setIncomeType(v)}
            disabled={!classFilter}
            getOptionLabel={(o) => o?.type || ''}
            isOptionEqualToValue={(a, b) => a.trans === b.trans}
            renderInput={(p) => (
              <TextField {...p} label="Income type" required
                helperText={classFilter ? 'Then the collection' : 'Choose a class first'}
                InputProps={{ ...p.InputProps,
                  startAdornment: (
                    <>
                      <IncomeIcon fontSize="small" sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary' }} />
                      {p.InputProps.startAdornment}
                    </>
                  ) }} />
            )} />

          <TextField size="small" type="date" label="Start date"
            required
            InputLabelProps={{ shrink: true }}
            value={startDate} onChange={(e) => setStartDate(e.target.value)}
            helperText="Applied to first-time registrations only" />

          <TextField size="small" type="number" label="Default daily amount (GHS)"
            value={defaultAmount}
            onChange={(e) => setDefaultAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right', ...TNUM } }}
            InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
            helperText="Optional — use Fill Blank to apply" />
        </Box>
      </Card>

      {/* ── Charging rule ────────────────────────────────────────
          Sits above the roster because it governs every row in it: the
          same "No pay" tap on the teacher's phone means arrears on
          school fees and nothing at all on feeding, and this is where
          that difference is decided. Collapsed by default — the summary
          line carries the answer, so the roster still starts near the
          top of the screen. */}
      {incomeType && (
        <Card sx={{ mb: 1.5, borderRadius: 2 }}>
          <Box
            onClick={() => setRulesOpen(o => !o)}
            sx={{
              px: 2.5, py: 1.3, display: 'flex', alignItems: 'center',
              gap: 1, flexWrap: 'wrap', cursor: 'pointer',
              '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.03) },
            }}>
            <RuleIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={800}>
              Charging rule
            </Typography>
            <Chip size="small"
              color={ruleKey(draftRules) === 'compulsory' ? 'error'
                : ruleKey(draftRules) === 'only_when_used' ? 'success' : 'warning'}
              variant="outlined"
              label={RULE_PRESETS.find(p => p.key === ruleKey(draftRules))?.label || 'Custom'}
              sx={{ height: 20, fontWeight: 800 }} />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
              {Number(draftRules.charge_when_absent)
                ? 'Absent learners still owe the day'
                : 'Absent learners owe nothing'}
              {' · '}
              {Number(draftRules.charge_when_unpaid)
                ? '"No pay" goes to arrears'
                : '"No pay" adds nothing'}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {rulesDirty && (
              <Chip size="small" color="warning" label="Unsaved"
                sx={{ height: 20, fontWeight: 800 }} />
            )}
            {!rulesDirty && rulesSavedAt > 0 && (
              <Chip size="small" color="success" variant="outlined"
                icon={<CheckIcon />} label="Saved"
                sx={{ height: 20, fontWeight: 800 }} />
            )}
            <IconButton size="small"
              sx={{ transform: rulesOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform .18s' }}>
              <ExpandIcon fontSize="small" />
            </IconButton>
          </Box>

          <Collapse in={rulesOpen} unmountOnExit>
            <Box sx={{ px: 2.5, pb: 2, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', mt: 1.2, mb: 1 }}>
                How <strong>{incomeType.type}</strong> is charged. This applies from
                now on — days already marked keep the charge they were given,
                because that money may already have been receipted.
              </Typography>

              {/* The two named combinations, one tap each. */}
              <ToggleButtonGroup exclusive size="small"
                value={ruleKey(draftRules)}
                sx={{ mb: 1.5, flexWrap: 'wrap',
                  '& .MuiToggleButton-root': {
                    textTransform: 'none', fontWeight: 800, px: 1.4, py: 0.5,
                    display: 'block', textAlign: 'left', lineHeight: 1.25,
                  } }}>
                {RULE_PRESETS.map(p => (
                  <ToggleButton key={p.key} value={p.key}
                    onClick={() => setRule(p.rules)}>
                    {p.label}
                    <Typography variant="caption"
                      sx={{ display: 'block', fontWeight: 600, opacity: 0.7 }}>
                      {p.hint}
                    </Typography>
                  </ToggleButton>
                ))}
                {/* Only reachable by flipping the switches into a mix the
                    presets do not name — shown so that state has a label
                    rather than looking like nothing is selected. */}
                {ruleKey(draftRules) === 'custom' && (
                  <ToggleButton value="custom" disabled>
                    Custom
                    <Typography variant="caption"
                      sx={{ display: 'block', fontWeight: 600, opacity: 0.7 }}>
                      Your own mix
                    </Typography>
                  </ToggleButton>
                )}
              </ToggleButtonGroup>

              {RULE_PRESETS.find(p => p.key === ruleKey(draftRules)) && (
                <Alert severity="info" variant="outlined" icon={false}
                  sx={{ borderRadius: 1.5, py: 0.3, mb: 1.5, fontSize: 12 }}>
                  {RULE_PRESETS.find(p => p.key === ruleKey(draftRules)).blurb}
                </Alert>
              )}

              {/* The switches underneath, for anything the two presets
                  do not cover. */}
              <Stack spacing={0.4} sx={{ maxWidth: 900 }}>
                {[
                  {
                    field: 'charge_when_absent',
                    Icon: AbsentIcon,
                    title: 'Absent learners are still charged',
                    body: 'The day’s amount is owed even when the learner does not '
                        + 'come to school. So long as the name is on the roll the '
                        + 'arrears keep adding up, day after day.',
                    disabled: false,
                  },
                  {
                    field: 'charge_when_unpaid',
                    Icon: NoPayIcon,
                    title: 'Charge a learner who came but paid nothing',
                    body: 'Tapping “No pay” on the collector’s sheet puts the '
                        + 'day’s amount into arrears. Turn this off where nothing '
                        + 'is owed unless it was taken — came, did not eat, did not pay.',
                    disabled: false,
                  },
                  {
                    field: 'bill_unmarked_at_close',
                    Icon: UnmarkedIcon,
                    title: 'Charge learners nobody marked, when the day is closed',
                    body: Number(draftRules.charge_when_absent)
                      ? 'Closing the collection day books the day’s amount against '
                        + 'anyone left blank, instead of refusing to close over them. '
                        + 'A compulsory charge should not depend on whether someone '
                        + 'remembered to take the register.'
                      : 'Only available when absent learners are charged — otherwise '
                        + 'it would record rows worth nothing.',
                    disabled: !Number(draftRules.charge_when_absent),
                  },
                  {
                    field: 'auto_settle_days',
                    Icon: UnmarkedIcon,
                    title: 'Settle school days nobody closed',
                    body: Number(draftRules.bill_unmarked_at_close)
                      ? 'The switch above only fires when someone closes the day, so a '
                        + 'day left open — or never opened — charges nobody. With this '
                        + 'on, the system settles those days by itself, booking the '
                        + 'day’s amount against whoever is still unmarked. Only real '
                        + 'school days inside the term, never a day marked “no school”, '
                        + 'and never today. The day is left open on purpose, so a '
                        + 'collector who marks the learner afterwards replaces the '
                        + 'automatic charge instead of adding to it.'
                      : 'Only available when learners nobody marked are charged — '
                        + 'this is the same rule, applied to days nobody closed.',
                    disabled: !Number(draftRules.bill_unmarked_at_close),
                  },
                ].map(({ field, Icon, title, body, disabled }) => {
                  const on = !!Number(draftRules[field]);
                  return (
                    <Box key={field}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.2,
                        px: 1.2, py: 0.9, borderRadius: 1.5,
                        border: 1, borderColor: on ? 'primary.light' : 'divider',
                        bgcolor: (t) => on
                          ? alpha(t.palette.primary.main, 0.045) : 'transparent',
                        opacity: disabled ? 0.55 : 1,
                      }}>
                      <Icon fontSize="small"
                        sx={{ mt: 0.4, color: on ? 'primary.main' : 'text.disabled' }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 12.5 }}>
                          {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', lineHeight: 1.45 }}>
                          {body}
                        </Typography>
                      </Box>
                      <Switch size="small" checked={on} disabled={disabled}
                        onChange={(e) => setRule({ [field]: e.target.checked ? 1 : 0 })} />
                    </Box>
                  );
                })}
              </Stack>

              {/* How far back the sweep may reach. Shown only when it is
                  on, because it is meaningless otherwise — and it is the
                  one field here that decides whether switching this on
                  charges a term's worth of arrears or nothing at all. */}
              {!!Number(draftRules.auto_settle_days) && (
                <Box sx={{
                  mt: 1, px: 1.2, py: 1, borderRadius: 1.5,
                  border: 1, borderColor: 'warning.light',
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
                }}>
                  <Typography sx={{ fontWeight: 800, fontSize: 12.5 }}>
                    Settle days from
                  </Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', lineHeight: 1.45, mb: 0.8 }}>
                    Nothing before this date is ever touched. It starts at the day
                    you switch this on, so turning it on cannot suddenly post weeks
                    of arrears. Move it back only when you mean to settle history —
                    every school day between then and yesterday that nobody closed
                    will be charged.
                  </Typography>
                  <TextField
                    type="date" size="small"
                    value={draftRules.auto_settle_from || ''}
                    onChange={(e) => setRule({ auto_settle_from: e.target.value || null })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ maxWidth: 210 }}
                  />
                </Box>
              )}

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                <Button size="small" variant="contained" disableElevation
                  startIcon={rulesSaving
                    ? <CircularProgress size={14} color="inherit" />
                    : <SaveIcon fontSize="small" />}
                  disabled={!rulesDirty || rulesSaving}
                  onClick={saveRules}
                  sx={{ textTransform: 'none', fontWeight: 800 }}>
                  Save rule
                </Button>
                {rulesDirty && (
                  <Button size="small" onClick={() => setDraftRules(savedRules)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Undo
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  {rulesDirty
                    ? 'Not saved yet — collectors still see the old rule.'
                    : 'This is what collectors are working to now.'}
                </Typography>
              </Stack>
            </Box>
          </Collapse>
        </Card>
      )}

      {/* Roster table */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{
          px: 2.5, py: 1.4, borderBottom: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        }}>
          <SubsIcon color="primary" />
          <Typography variant="subtitle2" fontWeight={800}>Roster</Typography>
          {incomeType && (
            <>
              <Chip size="small" color="primary" variant="outlined"
                label={`${stats.registered} registered`} sx={{ fontWeight: 700 }} />
              <Chip size="small" variant="outlined"
                label={`${stats.unregistered} pending`} sx={{ fontWeight: 700 }} />
              {stats.arrears > 0 && (
                <Chip size="small" color="error" variant="outlined"
                  label={`Arrears GHS ${fmtMoney(stats.arrears)}`}
                  sx={{ fontWeight: 700 }} />
              )}
              {stats.credit > 0 && (
                <Chip size="small" color="success" variant="outlined"
                  label={`Credit GHS ${fmtMoney(stats.credit)}`}
                  sx={{ fontWeight: 700 }} />
              )}
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Search name or ID"
            value={search} onChange={(e) => setSearch(e.target.value)}
            disabled={!incomeType}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ) }}
            sx={{ width: 240 }} />
          <Button size="small"
            startIcon={bulkFilling
              ? <CircularProgress size={14} color="inherit" />
              : <AutoIcon fontSize="small" />}
            onClick={applyDefaultToBlank}
            disabled={bulkFilling || !incomeType || !defaultAmount
                      || Number(defaultAmount) <= 0}>
            {bulkFilling ? 'Registering…' : 'Fill blank with default'}
          </Button>
        </Box>

        <CardContent sx={{ pt: 1 }}>
          {!classFilter ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
              Pick a <strong>class</strong> to begin. The roster loads one class
              at a time, so the page stays quick however large the school is.
            </Alert>
          ) : !incomeType ? (
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 1.5 }}>
              Now pick an income type to load <strong>{classFilter}</strong>.
            </Alert>
          ) : loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
          ) : roster.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
              <SubsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography fontWeight={700}>
                No students found{classFilter ? ` in ${classFilter}` : ''}.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined"
              sx={{ borderRadius: 1.5, maxHeight: '70vh' }}>
              <Table size="small" stickyHeader sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50', fontSize: 11, fontWeight: 800,
                  textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary',
                },
                '& tbody .MuiTableRow-root:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 260 }}>Student</TableCell>
                    <TableCell sx={{ width: 120 }}>Index</TableCell>
                    <TableCell sx={{ width: 120 }}>Class</TableCell>
                    <TableCell sx={{ width: 100 }}>Status</TableCell>
                    {packages.length > 0 && (
                      <TableCell sx={{ width: 170 }}>Package</TableCell>
                    )}
                    <TableCell align="right" sx={{ width: 200 }}>Daily Amount (GHS)</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>Arrears</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>Credit</TableCell>
                    <TableCell align="center" sx={{ width: 80 }}>Active</TableCell>
                    <TableCell align="right" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRoster.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={packages.length > 0 ? 10 : 9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No matches for "{search}"
                      </TableCell>
                    </TableRow>
                  )}
                  {pageRoster.map(r => {
                    const st = rowState[r.student_id] || {};
                    const isReg = !!r.subscription_id;
                    const isInactive = isReg && !r.active;
                    const recentlySaved = st.savedAt && (Date.now() - st.savedAt) < 2500;
                    return (
                      <TableRow key={r.student_id} sx={{
                        '& td': { fontSize: 12, py: 0.7 },
                        opacity: isInactive ? 0.55 : 1,
                      }}>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <Stack direction="row" spacing={0.7} alignItems="center">
                            <PersonIcon fontSize="inherit" color={isReg ? 'primary' : 'disabled'} />
                            {r.student_name || '—'}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ ...TNUM }}>{r.student_id}</TableCell>
                        <TableCell>
                          {r.class_name
                            ? <Chip size="small" label={r.class_name} variant="outlined"
                                sx={{ height: 20, fontWeight: 700 }} />
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {isReg
                            ? <Chip size="small" color={r.active ? 'success' : 'default'}
                                variant="outlined"
                                label={r.active ? 'Registered' : 'Inactive'}
                                sx={{ height: 20, fontWeight: 700 }} />
                            : <Chip size="small" variant="outlined"
                                label="Pending"
                                sx={{ height: 20, fontWeight: 700 }} />}
                        </TableCell>
                        {packages.length > 0 && (
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={r.package_id ?? ''}
                                onChange={(e) => assignPackage(r, e.target.value)}
                                displayEmpty
                                renderValue={(v) => {
                                  if (v === '' || v == null) {
                                    // Historical row that still remembers its
                                    // (now-deactivated) package via the snapshot.
                                    if (r.package_label) {
                                      return (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <PackageIcon fontSize="inherit" sx={{ color: 'text.disabled' }} />
                                          <Typography component="span" variant="caption"
                                            sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                            {r.package_label}
                                          </Typography>
                                        </Stack>
                                      );
                                    }
                                    return <Typography component="span" variant="caption" color="text.disabled">— none —</Typography>;
                                  }
                                  const p = packages.find(x => x.id === v);
                                  return (
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <PackageIcon fontSize="inherit" color="primary" />
                                      <Typography component="span" variant="caption" fontWeight={700}>
                                        {p?.label || r.package_label || ''}
                                      </Typography>
                                    </Stack>
                                  );
                                }}
                                sx={{ fontSize: 12,
                                  '& .MuiSelect-select': { py: 0.6 } }}
                              >
                                <MenuItem value=""><em>— None —</em></MenuItem>
                                {packages.map(p => (
                                  <MenuItem key={p.id} value={p.id}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                                      <PackageIcon fontSize="small" color="primary" />
                                      <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
                                        {p.label}
                                      </Typography>
                                      <Chip size="small" variant="outlined"
                                        label={`GHS ${fmtMoney(p.default_amount)}`}
                                        sx={{ height: 20, fontWeight: 700 }} />
                                    </Stack>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                            <TextField size="small" type="number"
                              value={st.amount ?? ''}
                              onChange={(e) => scheduleSave(r, e.target.value)}
                              placeholder={defaultAmount ? String(defaultAmount) : '—'}
                              error={!!st.error}
                              inputProps={{ min: 0, step: '0.01',
                                style: { textAlign: 'right', ...TNUM, padding: 4 } }}
                              sx={{ width: 130 }} />
                            <Box sx={{ width: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {st.saving && <CircularProgress size={14} />}
                              {!st.saving && recentlySaved && (
                                <CheckIcon fontSize="small" sx={{ color: 'success.main' }} />
                              )}
                            </Box>
                          </Stack>
                          {st.error && (
                            <Typography variant="caption" color="error"
                              sx={{ display: 'block', textAlign: 'right' }}>
                              {st.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{
                          ...TNUM, color: Number(r.arrears) > 0 ? 'error.main' : 'text.secondary',
                          fontWeight: Number(r.arrears) > 0 ? 800 : 500,
                        }}>
                          {isReg ? fmtMoney(r.arrears) : '—'}
                        </TableCell>
                        <TableCell align="right" sx={{
                          ...TNUM, color: Number(r.credit) > 0 ? 'success.main' : 'text.secondary',
                          fontWeight: Number(r.credit) > 0 ? 800 : 500,
                        }}>
                          {isReg ? fmtMoney(r.credit) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Switch size="small" disabled={!isReg}
                            checked={!!r.active}
                            onChange={() => toggleActive(r)} />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isReg ? 'Remove subscription' : 'Not registered'}>
                            <span>
                              <IconButton size="small" color="error" disabled={!isReg}
                                onClick={() => removeRow(r)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Only when more remain. The counts in the header describe
              the whole roster; this says how much of it is drawn. */}
          {hiddenCount > 0 && (
            <Box sx={{ pt: 1.2, textAlign: 'center' }}>
              <Button size="small" variant="outlined"
                onClick={() => setVisibleCount((n) => n + PAGE)}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Show {Math.min(PAGE, hiddenCount)} more
              </Button>
              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', mt: 0.4 }}>
                Showing {pageRoster.length} of {filteredRoster.length} students
                {' in '}{classFilter}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
